/** Shared vocabulary for the server, the client and the tests. */

import type { HeightfieldData } from './heightfield.js';

export type { HeightfieldData };

// ---------------------------------------------------------------------------
// Geography
// ---------------------------------------------------------------------------

export interface BBox {
  south: number;
  west: number;
  north: number;
  east: number;
}

export interface LatLon {
  lat: number;
  lon: number;
}

/** A point in world space, metres, origin at the region centre. Y is up. */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

// ---------------------------------------------------------------------------
// Raw OSM (the subset of the Overpass `out geom` response we care about)
// ---------------------------------------------------------------------------

export type OsmTags = Record<string, string>;

/**
 * A standalone tagged node — a point of interest. Overpass only hands these back for the
 * `node[...]` clauses in our query; the nodes that make up a way's geometry arrive inline
 * in `OsmWay.geometry` and never appear as elements of their own.
 */
export interface OsmNode {
  type: 'node';
  id: number;
  tags?: OsmTags;
  lat: number;
  lon: number;
}

export interface OsmWay {
  type: 'way';
  id: number;
  tags?: OsmTags;
  /** Node ids in order. Present in our baked fixtures; used to find junctions. */
  nodes?: number[];
  geometry?: LatLon[];
}

export interface OsmRelationMember {
  type: 'way' | 'node' | 'relation';
  ref: number;
  role: string;
  geometry?: LatLon[];
}

export interface OsmRelation {
  type: 'relation';
  id: number;
  tags?: OsmTags;
  members?: OsmRelationMember[];
}

export type OsmElement = OsmNode | OsmWay | OsmRelation;

export interface OsmResponse {
  elements: OsmElement[];
  /** Added by our bake step so a fixture knows the window it was cut from. */
  bbox?: BBox;
}

// ---------------------------------------------------------------------------
// The compact world description
// ---------------------------------------------------------------------------

export type RoadClass =
  | 'motorway'
  | 'trunk'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'residential'
  | 'service'
  | 'track'
  | 'path';

export interface RoadSpec {
  /** Full carriageway width in metres for a two-way road of this class. */
  width: number;
  /** Multiplier applied to the car's top speed while on this surface. */
  speed: number;
  color: number;
  /** Cosmetic-only surfaces are drawn but are not drivable and not routable. */
  drivable: boolean;
  /** Render order offset, so motorways sit above footpaths at crossings. */
  layer: number;
}

export interface Road {
  id: number;
  cls: RoadClass;
  name?: string;
  width: number;
  oneway: boolean;
  /** Height above the ground plane; higher classes sit on top at crossings. */
  y: number;
  /** Flat [x0,z0, x1,z1, ...] polyline in world metres. */
  pts: number[];
  /** Parallel to `pts`; OSM node id per vertex (-1 for a vertex made by bbox clipping). */
  nodes: number[];
  /**
   * A deck carried over the ground rather than lying on it, and which way.
   *
   * This is the one piece of road geometry terrain cannot be allowed to decide. Draping a
   * bridge on the heightfield follows the riverbed: measured across the Vistula, Most
   * Grunwaldzki sags 6.2 m below its own abutments and the deck ends up at or under the
   * water surface, so the car drives through the river it is supposed to be crossing. The
   * renderer interpolates a `bridge` deck between its ends instead of sampling along it,
   * and pushes a `tunnel` the other way so it does not erupt out of a hillside.
   */
  deck?: 'bridge' | 'tunnel';
  /**
   * OSM `layer`, where it is not zero. Only ever used to decide which of two bridges that
   * cross each other is on top — see `decks.ts`.
   */
  layer?: number;
  /**
   * Part of the circulating carriageway of a roundabout. Nobody paints a zebra across the
   * ring, and every node where a street meets it is a junction, so without this the ring
   * was striped all the way round.
   */
  roundabout?: true;
  /**
   * Width of the pavement outboard of the kerb stone on side 0 (left of travel) and side 1,
   * where a mapped sidewalk says it differs from `pavementWidthFor`. Absent means the default
   * on both sides; read it through `pavementSides`, never directly. DECISIONS.md §93.
   */
  pavement?: [number, number];
}

/**
 * What a building reads as from the street. Derived from `building=*` where OSM bothers to
 * say, from the points of interest sitting inside the footprint where it doesn't — which is
 * most of the time, because 57% of Kraków's buildings, 79% of Manhattan's and 32% of Paris's
 * carry nothing but `building=yes` — and from the height as a last resort.
 *
 * `plain` is the honest "we have no idea": a mid-rise block with no tag and nothing inside it.
 */
export type BuildingKind =
  | 'house'
  | 'apartment'
  | 'shop'
  | 'office'
  | 'tower'
  | 'hotel'
  | 'civic'
  | 'church'
  | 'industrial'
  | 'parking'
  | 'fuel'
  | 'shed'
  | 'plain';

/**
 * How a building's wall is drawn — which of the facade archetypes its windows come from.
 *
 * Separate from `BuildingKind` on purpose, and the distinction is the whole point of it.
 * A kind says what a building is *for*; a style says what era and construction it looks
 * like, and the two are close to independent. A block of flats is `apartment` whether it is
 * an 1880s tenement or a 1970s slab, and drawing both with the same window is most of why
 * a generated city reads as a repeated stamp.
 *
 * There is deliberately no `era` tag behind this. OSM has one — `start_date` — and it is
 * not usable: it survives on a low single-digit percentage of footprints outside the few
 * countries where a national import supplied it, and none of the four regions we ship is
 * one of them. What OSM *does* carry almost everywhere is the storey count, which is the
 * better signal anyway once it is read relative to the city it is in (see `facadeStyleFor`).
 *
 * - `period`    — pre-industrial and 19th-century: tall narrow punched windows, deep sills.
 * - `ornate`    — the grander version of the same: shutters, a balcony rail, a cornice band.
 * - `modern`    — post-war: wider openings, a spandrel panel under each, thin mullions.
 * - `curtain`   — glass: full-width glazing in continuous floor bands, almost no wall.
 * - `utility`   — sheds, works and car parks: industrial glazing, mostly blank panel.
 * - `vernacular`— small and domestic: a couple of small windows in a lot of plain wall.
 */
export type FacadeStyle = 'period' | 'ornate' | 'modern' | 'curtain' | 'utility' | 'vernacular';

/** Fixed order, and it is load-bearing: the index into it is what the facade shader branches on. */
export const FACADE_STYLES: readonly FacadeStyle[] = [
  'period',
  'ornate',
  'modern',
  'curtain',
  'utility',
  'vernacular',
];

/**
 * What a wall is made of, which is a separate question from what era it is (`FacadeStyle`).
 * A 19th-century terrace is rendered in Kraków, red brick in Manhattan and limestone in the
 * Marais, and drawing all three the same way is most of why one city looked like another.
 *
 * - `render`  — smooth plaster, painted. Most of central Europe.
 * - `brick`   — running bond, mortar lines, a tint per brick.
 * - `stone`   — ashlar: large blocks, fine joints.
 * - `panel`   — prefabricated concrete, one panel per bay and storey: the Polish housing estate.
 * - `glass`   — the curtain wall itself.
 * - `metal`   — ribbed cladding on sheds and works.
 */
export type FacadeMaterial = 'render' | 'brick' | 'stone' | 'panel' | 'glass' | 'metal';

/** Fixed order, and it is load-bearing in the same way as `FACADE_STYLES`. */
export const FACADE_MATERIALS: readonly FacadeMaterial[] = [
  'render',
  'brick',
  'stone',
  'panel',
  'glass',
  'metal',
];

export interface Building {
  id: number;
  height: number;
  minHeight: number;
  /**
   * Whether the car collides with it. False for buildings a drivable road legitimately
   * passes through — an archway, a tunnel, a viaduct on another layer. They are still
   * drawn; they just don't wall the street off.
   */
  solid: boolean;
  kind: BuildingKind;
  /** Wall colour. */
  color: number;
  /**
   * Roof colour, and the shape the mesh builder should give the roof.
   *
   * Both live in the payload rather than being re-derived on the client because every look
   * decision in the game is made once, in `classify.ts`, where the unit tests can pin it
   * down — and because two clients disagreeing about what colour a roof is would be a
   * determinism bug in a game whose whole premise is that everyone races the same city.
   */
  roof: number;
  roofStyle: 'flat' | 'pitched' | 'plant';
  /**
   * Which facade archetype the walls are drawn from. See `FacadeStyle`.
   *
   * In the payload rather than re-derived on the client for the same reason `roof` and
   * `base` are: the look is decided once, in `classify.ts`, where a unit test can pin it
   * down, and two players racing the same city have to be looking at the same buildings.
   */
  style: FacadeStyle;
  /** What the walls are made of. See `FacadeMaterial`; `color` is chosen to match it. */
  material: FacadeMaterial;
  /**
   * Ground-floor band colour, or null where there is no shopfront to draw.
   *
   * This is the cheapest thing in the generator that changes how a city reads. A street of
   * extruded blocks is a street of extruded blocks; give the ones with a bakery or a bar
   * inside them a different ground floor and the same geometry becomes a high street.
   */
  base: number | null;
  /**
   * The name over the shopfront: the named shop, café or hotel inside, from OSM. Null where
   * there is no band to put it on, or nothing inside has a name.
   */
  sign: string | null;
  /** Flat [x0,z0, ...] closed outer ring, counter-clockwise, no repeated last point. */
  poly: number[];
  /** Inner rings (courtyards), same encoding. */
  holes: number[][];
}

export type AreaKind =
  | 'water'
  | 'park'
  | 'grass'
  | 'wood'
  | 'sand'
  | 'pitch'
  | 'paved'
  /** A car park on the ground — not one under it, on a roof, or in a building. */
  | 'parking'
  | 'urban';

/**
 * What a pitch is marked out for, for the sports whose markings are drawn. Anything else —
 * a skate park, a riding arena, a pitch nobody tagged — is a coloured surface and a fence.
 */
export type PitchSport = 'soccer' | 'basketball' | 'tennis' | 'volleyball' | 'handball';

export interface Area {
  id: number;
  kind: AreaKind;
  color: number;
  /** Height above the ground plane, pre-computed to keep coplanar areas from z-fighting. */
  y: number;
  poly: number[];
  holes: number[][];
  /** On a `pitch`, the sport its lines and goals are for, where OSM says. */
  sport?: PitchSport;
}

/**
 * A roof on posts — OSM's `building=roof`: a fuel station's canopy, a carport, a market roof.
 *
 * Not a `Building`, because it is not a wall: it was one until this existed, a four-metre
 * solid box the car stopped dead against, standing where every filling station's forecourt
 * should have been. A car drives under one.
 */
export interface Canopy {
  id: number;
  /** Outer ring, counter-clockwise, as `Building.poly`. */
  poly: number[];
  /** Underside and top, metres above the lowest ground under it. */
  bottom: number;
  top: number;
  /** Over a fuel station's forecourt: it gets pumps under it and the station's name on it. */
  fuel: boolean;
  name: string | null;
  /** `brand:colour` or `building:colour`, where somebody mapped one; else a neutral palette. */
  colour: number | null;
}

/**
 * A shop big enough to be its own building: a supermarket, a shopping centre, a fuel
 * station's kiosk. It gets a glazed entrance and its name in large letters, instead of the
 * shopfront band and fascia board a unit in a terrace gets.
 */
export type StoreKind = 'supermarket' | 'mall' | 'kiosk';

export interface Store {
  /** The `Building.id` it occupies. */
  building: number;
  kind: StoreKind;
  /** From OSM's `name`, else `brand`. Text only: nobody's logo is drawn. */
  name: string | null;
  colour: number | null;
}

/** A café, bar or restaurant with tables outside, at its OSM position. */
export interface Terrace {
  id: number;
  x: number;
  z: number;
  /** The `Building.id` it is mapped inside, or -1 for one mapped on the pavement itself. */
  building: number;
}

/**
 * What stands in the city besides its buildings and trees, decided from OSM tags that only
 * the server has — see DECISIONS.md §96. Where each table and pump goes is worked out on the
 * client from these, the same way for everybody.
 */
export interface CityObjects {
  canopies: Canopy[];
  stores: Store[];
  terraces: Terrace[];
}

/**
 * A disc dropped where two or more roads share an OSM node. Without these, mitred road
 * ribbons meeting at a crossroads leave a notch at every corner; with them the junction
 * reads as a continuous piece of tarmac.
 */
export interface Junction {
  x: number;
  z: number;
  r: number;
  color: number;
  y: number;
}

/**
 * What a railway line is, for drawing it and for deciding what runs on it. `subway` is only
 * ever seen where it comes up out of the ground; underground, nothing is drawn.
 */
export type RailKind = 'rail' | 'light_rail' | 'tram' | 'narrow_gauge' | 'subway';

/** One OSM railway way, clipped to the region like a road. See DECISIONS.md §95. */
export interface RailLine {
  id: number;
  kind: RailKind;
  /** Flat [x0,z0, ...] centreline in world metres, like `Road.pts`. */
  pts: number[];
  /** OSM node id per vertex, -1 where clipping made one. What joins lines into a network. */
  nodes: number[];
  /**
   * A yard, a siding, a spur or a crossover (`service=*`). Drawn, and no train is scheduled
   * along one: a train taking a siding on its way through a station is the one thing a
   * passenger would notice.
   */
  siding?: true;
  deck?: 'bridge' | 'tunnel';
  layer?: number;
  /**
   * Laid in a street rather than on its own ballast: a tram line running down the middle of
   * the carriageway. Drawn flush with the tarmac, and its surface is the street's.
   */
  embedded?: true;
  /** Carries an overhead line, so it gets masts. */
  electrified?: true;
}

/** A platform, as an outline (closed way) or as a line with a nominal width. */
export interface RailPlatform {
  id: number;
  /** Closed: a ring like `Building.poly`. Open: a centreline. */
  pts: number[];
  closed: boolean;
}

/**
 * Where a road crosses a railway on the level, and what stands there.
 *
 * Found two ways: an OSM node the road and the railway share (tagged `railway=level_crossing`
 * or not — an untagged one is a mapping slip, not a different kind of crossing), and every
 * other place a drivable road and a railway at the same level cross without one. Tracks a
 * few metres apart over the same road are one crossing with one set of gear.
 */
export interface LevelCrossing {
  /** The shared OSM node, or a hash of the two ways for a crossing found by geometry. */
  id: number;
  /** Centre: on the road's centreline, midway between the outermost tracks. */
  x: number;
  z: number;
  /** Unit vector along the road. */
  dx: number;
  dz: number;
  /** Carriageway half-width at the crossing. */
  half: number;
  /** Distance along the road from the centre to the outermost rail, either way. */
  reach: number;
  roadId: number;
  /** Every rail line through it. */
  lines: number[];
  /** Boom barriers across the approach lanes. */
  barriers: boolean;
  /** Flashing red lights. A crossing with neither is a St Andrew's cross on a post. */
  lights: boolean;
}

/** Somewhere a train or a tram stops: a station, a halt or a tram stop, from its OSM node. */
export interface RailStop {
  x: number;
  z: number;
  kind: 'station' | 'halt' | 'tram_stop';
}

/** Everything about the railway the region contains. Absent where it has none. */
export interface RailNetwork {
  lines: RailLine[];
  platforms: RailPlatform[];
  crossings: LevelCrossing[];
  stops: RailStop[];
}

export interface Checkpoint {
  index: number;
  /** Gate centre. */
  x: number;
  z: number;
  /** Unit vector along the road at this point (the direction of travel). */
  dx: number;
  dz: number;
  /** Half-width of the gate in metres. */
  radius: number;
}

export interface WorldStats {
  roadCount: number;
  buildingCount: number;
  areaCount: number;
  junctionCount: number;
  /** Points of interest found inside the region — the signal that types the buildings. */
  poiCount: number;
  roadMetres: number;
  /** Estimated triangles the client will build for this world. */
  estimatedTriangles: number;
  generationMs: number;
}

export interface World {
  /** Bumped whenever the generator's output format or geometry rules change. */
  version: number;
  /** Stable id: hash of (regionKey, seed, generator version). */
  id: string;
  seed: number;
  regionKey: string;
  bbox: BBox;
  /** Projection origin — the region centre. */
  origin: LatLon;
  /** Half-extent of the world in metres, for ground plane / minimap sizing. */
  extent: { x: number; z: number };
  roads: Road[];
  buildings: Building[];
  areas: Area[];
  junctions: Junction[];
  /** Canopies, big stores and café terraces. Absent from worlds generated before them. */
  objects?: CityObjects;
  /**
   * Terrain, as a displacement from the Y = 0 plane. Absent when the region is genuinely
   * level, when the DEM could not be fetched, or when the server is running offline — the
   * game must be playable in all three cases, so every consumer treats a missing field as
   * `Heightfield.flat()` rather than as an error.
   */
  ground?: HeightfieldData;
  /**
   * Mapped street lamps (`highway=street_lamp`), flat `[x0,z0, x1,z1, ...]`. Absent where none
   * are mapped, which is most places and every committed fixture.
   */
  lamps?: number[];
  /**
   * Tracks, platforms and level crossings. Absent for a region with no railway, and for every
   * world built before railways were read — including the committed fixtures.
   */
  rail?: RailNetwork;
  /** Race route: start, checkpoints in order, finish. */
  route: {
    start: Checkpoint;
    finish: Checkpoint;
    checkpoints: Checkpoint[];
    /** The full driving line, [x,z,...] — used by the minimap and the AI-free arrow. */
    path: number[];
    lengthMetres: number;
  };
  stats: WorldStats;
}

/** What the host picks in the region picker. */
export interface RegionRequest {
  bbox: BBox;
  start?: LatLon;
  finish?: LatLon;
  /**
   * Which way the grid faces, in degrees clockwise from north — a compass bearing, because
   * that is the thing the picker draws an arrow for on a north-up map. Absent means
   * whichever way the shortest route happens to leave the start, which is what every race
   * was before this existed.
   */
  startBearing?: number;
  /**
   * Gates the race must pass through, in order.
   *
   * These are waypoints, not decorations: the route is planned start → each of them →
   * finish, so they choose which streets the race uses rather than merely marking a line
   * somebody else chose. Absent means the gates are spaced evenly along whatever line the
   * router picks.
   */
  checkpoints?: LatLon[];
  /** How many evenly-spaced gates to drop. Ignored when `checkpoints` is given. */
  checkpointCount?: number;
  seed?: number;
  /** Fixture id, when racing offline. */
  fixture?: string;
}

// ---------------------------------------------------------------------------
// Tours — a saved playlist of regions, raced back to back for points
// ---------------------------------------------------------------------------

/** One race in a tour: a region, plus the name it goes by on the standings board. */
export interface TourStop {
  /** Stable within its tour, so a standings row can name the race it came from. */
  id: string;
  title: string;
  region: RegionRequest;
}

/**
 * A tiny top-down sketch of one stop, drawn on its card in the tour browser.
 *
 * Vectors rather than an image, and captured by the browser that built the tour rather than
 * rendered by the server. A headless Chromium on the target box — one vCPU — would spend
 * more memory on a thumbnail than the game does on a city, and this costs a few kilobytes
 * of numbers the region picker had already fetched. See DECISIONS.md §48.
 */
export interface StopPreview {
  /** Road centrelines, each a flat `[x, y, x, y, …]` run in the box below. */
  lines: number[][];
  /** The racing route through them, same coordinates. */
  route: number[];
  /** `[minX, minY, maxX, maxY]` — what to fit the card's viewBox to. */
  box: [number, number, number, number];
}

export interface Tour {
  id: string;
  name: string;
  /**
   * The account that saved it, copied in at save time. Since accounts arrived this is a
   * checked identity rather than a free-text signature — except on the handful of tours
   * imported from before they existed, where it is still whatever was typed.
   */
  author: string;
  createdAt: number;
  updatedAt: number;
  stops: TourStop[];
  /** One per stop, in order. Absent on tours saved before previews existed. */
  preview?: StopPreview[];
}

/** What the lobby's tour list shows without fetching every tour in full. */
export interface TourSummary {
  id: string;
  name: string;
  author: string;
  updatedAt: number;
  stopCount: number;
  stopTitles: string[];
  /** The owning account's current name, or null for the frozen pre-account tours. */
  owner: string | null;
  /** Ownerless: listed and raceable, refused by every write path. */
  frozen: boolean;
  /** Whether the account asking owns it, and may therefore edit or delete it. */
  mine: boolean;
  /** Shipped with the server rather than built by anybody — see `ensureSeeded`. */
  curated: boolean;
  /** Thumbs up and down, from signed-in drivers, one each. */
  up: number;
  down: number;
  /** How the account asking voted: 1, -1, or 0 for not at all (and for nobody signed in). */
  myVote: -1 | 0 | 1;
  /** The first stop's sketch, for the card. The rest are fetched with the tour itself. */
  preview?: StopPreview;
}

export interface TourStanding {
  id: string;
  name: string;
  color: number;
  ai?: boolean;
  points: number;
  /** Points won in the race that just finished. Null before any race has been run. */
  lastPoints: number | null;
  /** Finishing position at each completed stop, in order. Null where they did not classify. */
  positions: (number | null)[];
  /** Stops won outright — the first tie-break. */
  wins: number;
  /**
   * Races actually completed, which is *not* `positions.filter(Boolean).length` — everyone
   * on the grid gets a classification whether or not they crossed the line. It is the
   * second tie-break, and it has to sit above total time; see `compareStandings`.
   */
  finishes: number;
  /** Summed finishing times, the last tie-break. Races not finished contribute nothing. */
  totalTimeMs: number;
  /** Place in the standings, 1-based, after the last completed race. */
  rank: number;
  /** Places gained (+) or lost (−) in the last race. Zero until two races are in. */
  delta: number;
}

export interface TourState {
  id: string;
  name: string;
  stops: { id: string; title: string }[];
  /** The stop loaded or being raced, 0-based. */
  index: number;
  /** How many stops already have a result. */
  completed: number;
  standings: TourStanding[];
  /** True once every stop has been raced — the cue for the podium. */
  complete: boolean;
}

// ---------------------------------------------------------------------------
// Weather at the region, now
// ---------------------------------------------------------------------------

export type SkyPhase = 'night' | 'twilight' | 'golden' | 'day';
export type WeatherKind = 'clear' | 'cloudy' | 'overcast' | 'fog' | 'rain' | 'storm' | 'snow';

/**
 * Where a lobby's weather comes from: the real sky over the real place right now, which is
 * the default and the point of the game, or a time and a sky the host chose — for a night
 * race at noon, or a sunny lap of a city that is under rain. See DECISIONS.md §90.
 */
export type WeatherChoice =
  | { real: true }
  | {
      real: false;
      /** Local time at the region, hours from midnight, in quarter hours. */
      hour: number;
      kind: WeatherKind;
    };

/**
 * The conditions the race is run in, taken from the real world at the real place.
 *
 * The sun is arithmetic and always available. Everything else is an observation that may
 * not arrive — see `observed`, and never branch on the weather without handling its
 * absence.
 */
export interface Weather {
  /** Solar elevation in degrees at the region right now. Negative is below the horizon. */
  sunAltitude: number;
  /** Solar azimuth, degrees clockwise from north. */
  sunAzimuth: number;
  phase: SkyPhase;
  kind: WeatherKind;
  /** Cloud cover, 0–1. */
  cloud: number;
  /** How hard it is coming down, 0–1. Zero unless `kind` is rain, storm or snow. */
  precipitation: number;
  temperatureC: number | null;
  windKph: number | null;
  /** Wall-clock time at the region, HH:MM on a 24-hour clock. */
  localTime: string;
  utcOffsetMinutes: number;
  /** False when this is the fair-weather default rather than a real observation. */
  observed: boolean;
  /** One line for the UI: "Overcast · 17:42 local · 6 °C". */
  label: string;
}

// ---------------------------------------------------------------------------
// Lobby + race protocol
// ---------------------------------------------------------------------------

/**
 * `staging` sits between a built world and the countdown: everyone is looking at the
 * region from the air and saying whether they are ready. It is a phase of its own rather
 * than a flag on `generating` because the *server* has finished building by then — what is
 * left is each client fetching that world and saying so, which is a different thing to be
 * told. The phase ends only when every client has (see `WorldStatus`).
 */
export type LobbyPhase =
  | 'lobby'
  | 'generating'
  | 'staging'
  | 'countdown'
  | 'racing'
  | 'finished';

/**
 * Whether a client has the world the lobby is staging.
 *
 * The server builds a world; every client then has to *download* it and build its meshes,
 * which on an uncached city is the slowest thing that happens between pressing start and
 * driving. `ready` is about a person, this is about their browser, and the lights need
 * both — see `LobbyPhase` and DECISIONS.md §53.
 */
export type WorldStatus = 'loading' | 'ready' | 'failed';

/**
 * What the lobby does with a world once it has one. `free` is a drive round it with no gates,
 * no clock and no result: it goes straight from the build to `racing`, with no grid and no
 * countdown, people can join while it is on, and it lasts until the host ends it. See
 * DECISIONS.md §88.
 */
export type LobbyMode = 'race' | 'free';

export interface PlayerPublic {
  id: string;
  name: string;
  color: number;
  ready: boolean;
  host: boolean;
  connected: boolean;
  /**
   * Where this client is with the lobby's current world. Always `ready` for an AI, and
   * `loading` for everybody back in the lobby, where there is no world to have yet — it
   * only says something from `staging` onwards.
   */
  worldStatus: WorldStatus;
  /**
   * A server-driven opponent. They appear in the lobby, the snapshot stream and the results
   * exactly like anyone else — the flag exists so the UI can say so, not so the race can
   * treat them differently.
   */
  ai?: boolean;
  /** Index of the next checkpoint this player must cross. */
  nextCheckpoint: number;
  finishedAt: number | null;
  finishPosition: number | null;
  /** Server-side race progress in metres along the route, for live positions. */
  progress: number;
}

export interface LobbyPublic {
  code: string;
  phase: LobbyPhase;
  mode: LobbyMode;
  players: PlayerPublic[];
  region: RegionRequest | null;
  /**
   * What the host called the region when they picked it — a tour's stop, a saved drawing —
   * or null for a built-in region, which is named by its fixture, and for one never named.
   */
  regionTitle: string | null;
  worldId: string | null;
  /** Server clock ms at which the race starts. Null until the countdown begins. */
  startsAt: number | null;
  /** Server clock ms when the race actually started. */
  startedAt: number | null;
  /** How many AI opponents the host has asked for. */
  aiCount: number;
  /** The cup in progress, or null for a one-off race. */
  tour: TourState | null;
  /** Conditions at the region, or null when they have not been resolved yet. */
  weather: Weather | null;
  /** Whether `weather` is the real one or the host's. */
  weatherChoice: WeatherChoice;
  /** What the server is doing while `phase` is `generating`. Null at every other time. */
  build: BuildProgress | null;
  error: string | null;
}

/**
 * What a world build is doing right now.
 *
 * The first race in an uncached city is a minute or more of Overpass, and until this
 * existed the whole of that minute looked identical to a hang: one disabled button reading
 * "Building the world…", no bar, no count, nothing that changed. Everything here is aimed
 * at the two questions somebody staring at that button is actually asking — is it still
 * working, and is it going to be like this every time.
 */
export interface BuildProgress {
  stage: 'osm' | 'terrain' | 'assembling' | 'ready';
  /** One short line naming the step. */
  label: string;
  /** A second line with the count, or empty. */
  detail: string;
  /** 0..1 across the whole build. Monotonic — it never goes backwards mid-build. */
  fraction: number;
  /** Server clock ms when this build started, for an elapsed readout. */
  startedAt: number;
  /**
   * True once the build is known to have gone to the network. A region already on disk
   * finishes in under a second and never sets this, which is what lets the UI promise that
   * the wait is a one-off rather than the normal cost of starting a race.
   */
  downloading: boolean;
  /**
   * Seconds the rest of the build should take, as of when this step was sent — null when
   * the server cannot say, which is whenever nothing is downloading. Counted down on the
   * client between steps, because steps arrive once per map tile and a tile can be forty
   * seconds of Overpass.
   */
  etaSeconds: number | null;
}

/** Per-player state broadcast at ~20 Hz. */
export interface PlayerSnapshot {
  id: string;
  /** position */
  x: number;
  y: number;
  z: number;
  /** heading in radians */
  h: number;
  /** speed in m/s, signed */
  v: number;
  /** next checkpoint index */
  c: number;
  /** progress in metres along route */
  p: number;
}

export type ClientMessage =
  | { t: 'hello'; code: string; name: string; color: number; playerId?: string }
  | { t: 'profile'; name: string; color: number }
  | { t: 'ready'; ready: boolean }
  /**
   * "I have the world" — or "I could not get it". Sent once per `world` message, after the
   * payload has downloaded and the meshes are built, and it is what holds the lights.
   */
  | { t: 'worldStatus'; worldId: string; status: 'ready' | 'failed' }
  | { t: 'region'; region: RegionRequest; title?: string }
  | { t: 'aiCount'; count: number }
  /** Host: race the region, or drive round it freely. */
  | { t: 'mode'; mode: LobbyMode }
  /** Host: the real weather and time, or a chosen one. */
  | { t: 'weatherChoice'; choice: WeatherChoice }
  /** Host: run this saved tour instead of a single region. Null goes back to one-off races. */
  | { t: 'tour'; tourId: string | null }
  | { t: 'start' }
  /** Host: stop waiting on a world that is taking too long. Cached tiles are kept. */
  | { t: 'cancelGeneration' }
  /** Host: load the next stop of the tour and stage it — or, in a free drive, drive it. */
  | { t: 'nextRace' }
  /** Host: abandon the tour where it stands and go back to the lobby. */
  | { t: 'endTour' }
  | { t: 'state'; x: number; y: number; z: number; h: number; v: number }
  | { t: 'checkpoint'; index: number }
  | { t: 'finish' }
  | { t: 'restart' }
  | { t: 'honk' }
  | { t: 'chat'; text: string }
  | { t: 'ping'; n: number };

export type ServerMessage =
  | { t: 'welcome'; playerId: string; lobby: LobbyPublic; now: number }
  | { t: 'lobby'; lobby: LobbyPublic }
  | { t: 'world'; worldId: string; url: string }
  | { t: 'countdown'; startsAt: number; now: number }
  | { t: 'snapshot'; now: number; players: PlayerSnapshot[] }
  | { t: 'progress'; players: Pick<PlayerPublic, 'id' | 'nextCheckpoint' | 'progress' | 'finishPosition' | 'finishedAt'>[] }
  | { t: 'finished'; results: RaceResult[] }
  | { t: 'error'; message: string }
  /** Somebody sounded their horn. Relayed to everyone but them. */
  | { t: 'honk'; id: string }
  | ({ t: 'chat' } & ChatMessage)
  /** Replayed once on join, so the last arrival does not walk into silence. */
  | { t: 'chatLog'; messages: ChatMessage[] }
  | { t: 'pong'; n: number; now: number };

/**
 * One line said in a lobby.
 *
 * The name and colour are stamped by the server from the player record rather than taken
 * from the message, so nobody speaks as anybody else — and they travel with the line so a
 * client that has since forgotten a player who left can still render what they said.
 */
export interface ChatMessage {
  id: string;
  name: string;
  color: number;
  text: string;
  at: number;
  /**
   * A line the server writes rather than the player: this car crossed the finish, in this
   * time from the start. `text` is empty, and each client says it in its reader's language.
   * The time and not the place, because a place can still change after it is announced —
   * see `Lobby.recordFinish` — and a time cannot.
   */
  finish?: { timeMs: number | null };
}

export interface RaceResult {
  id: string;
  name: string;
  color: number;
  position: number | null;
  timeMs: number | null;
  checkpointsPassed: number;
  finished: boolean;
  connected: boolean;
  ai?: boolean;
  /** Cup points won here. Only present when the race was a stop on a tour. */
  points?: number;
}
